<template>
    <div id="w3-channels">
        <AppBreadCrumb :paths="[{ title: $t('whatsappV3.channelsTitle') }]" :select="true"></AppBreadCrumb>
        <div style="background-color: #ffffff" class="d-flex right-page">
            <div class="ml-3 mt-3" style="flex: 0 0 100%">
                <div class="big-title">{{ $t("whatsappV3.channelsTitle") }}</div>
                <div class="channels">
                    <div
                        class="story channel-add"
                        @click="$feature.beforeCreateIntegrationChannel(createChannel, 'whatsapp-unipile')"
                    >
                        <a-icon type="plus-circle" theme="filled" style="font-size: 30px" />
                        <span class="text-large font-weight-bolder mt-2" style="font-size: 20px; letter-spacing: 2px">
                            {{ $t("whatsappChannel.addChannel") }}
                        </span>
                    </div>
                    <div
                        class="channel-item"
                        :class="{ disabled: !isChannelAccessible(channel) }"
                        v-for="(channel, index) in channels"
                        @click="() => isChannelAccessible(channel) && editChannel(channel)"
                        :key="index"
                    >
                        <div
                            v-if="needsReconnect(channel)"
                            class="qr-overlay"
                            @click.stop="handleResetChannel(channel)"
                        >
                            <div class="qr-overlay-content">
                                <a-icon type="qrcode" style="font-size: 40px" />
                                <span>{{ $t("whatsappV3.scanQr") }}</span>
                                <a-spin v-if="resetLoadingChannelId === channel._id" />
                            </div>
                        </div>
                        <div class="icon-container">
                            <WhatsappIcon />
                        </div>
                        <div class="name-container">
                            <span class="name">{{ channel.name.toUpperCase() }}</span>
                            <span class="phone"
                                >{{ $_.get(channel, "payload.name") }} - ({{
                                    $_.get(channel, "forward.story") || $t("whatsappV3.liveSupport")
                                }})</span
                            >
                        </div>

                        <div class="options">
                            <UnipileChannelStatusBadge :channel="channel" />
                            <a-dropdown :trigger="['click']" placement="bottomRight">
                                <div class="icon-container ant-dropdown-link" @click="(e) => e.stopPropagation()">
                                    <span class="material-icons">more_horiz</span>
                                </div>
                                <template #overlay>
                                    <a-menu>
                                        <a-menu-item v-if="needsReconnect(channel)" @click="handleResetChannel(channel)" style="display: flex; gap: 5px; align-items: center;">
                                            <i class="material-icons" style="color: #faad14;">wifi_off</i>
                                            <span>{{ $t("whatsappV3.reconnect") }}</span>
                                        </a-menu-item>
                                        <a-menu-item>
                                            <div
                                                class="icon-container"
                                                @click="$router.push(`/integrations/whatsapp-v3/${channel._id}/detail`)"
                                            >
                                                <md-icon style="margin-right: 10px">edit</md-icon>
                                                <span>{{ $t("message.edit") }}</span>
                                            </div>
                                        </a-menu-item>
                                        <a-menu-item
                                            @click="copyChannelId(channel._id)"
                                            style="display: flex; gap: 5px"
                                        >
                                            <i class="material-icons">content_copy</i>
                                            <span>{{ $t("whatsappV3.copyChannelId") }}</span>
                                        </a-menu-item>
                                        <a-menu-item>
                                            <div class="icon-container" @click="deleteWpBusiness(channel._id)">
                                                <md-icon style="margin-right: 10px">delete</md-icon>
                                                <span>{{ $t("chatbotv2.delete") }}</span>
                                            </div>
                                        </a-menu-item>
                                    </a-menu>
                                </template>
                            </a-dropdown>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <WhatsappSolutionComparisonModal
            :visible="showInfoModal"
            @cancel="handleInfoCancel"
            @accept="handleInfoAccept"
        />

        <Modal2
            :title="$t('whatsappV3.addChannelModalTitle')"
            :description="$t('whatsappV3.addChannelModalDesc')"
            class-name="new-waba"
            :showFooter="false"
            @cancel="showCreateForm = false"
            v-if="showCreateForm"
        >
            <NewWhatsappV3Channel @cancel="showCreateForm = false" @ok="showCreateForm = false" />
        </Modal2>
        <Modal2
            :title="$t('whatsappV3.updateChannelModalTitle')"
            :description="$t('whatsappV3.updateChannelModalDesc')"
            class-name="new-waba"
            :showFooter="false"
            @cancel="updatingChannel = null"
            v-if="updatingChannel"
        >
            <UpdateW3Channel
                @cancel="updatingChannel = false"
                @ok="updatingChannel = false"
                :channel="updatingChannel"
            />
        </Modal2>
        <a-modal
            :visible="deleteModal"
            icon="exclamation-circle"
            :title="$t('whatsappV3.deleteChannelTitle')"
            @cancel="deleteModal = false"
        >
            <p>{{ $t("whatsappV3.deleteChannelConfirm") }}</p>
            <template #footer>
                <a-button key="back" @click="deleteModal = false">
                    {{ $t("chatbotv2.dismiss") }}
                </a-button>
                <a-button key="submit" type="danger" @click="deleteWhatsappChannel">
                    {{ $t("chatbotv2.delete") }}
                </a-button>
            </template>
        </a-modal>

        <a-modal
            :visible="resetConfirmModal"
            icon="exclamation-circle"
            :title="$t('whatsappV3.resetConfirmTitle')"
            @cancel="resetConfirmModal = false"
        >
            <div class="reset-confirm-content">
                <a-alert type="warning" show-icon class="mb-3">
                    <template #message>
                        <strong>{{ $t("whatsappV3.warning") }}</strong>
                    </template>
                    <template #description>
                        <ul style="margin: 0; padding-left: 20px;">
                            <li>{{ $t("whatsappV3.resetWarning1") }}</li>
                            <li>{{ $t("whatsappV3.resetWarning2") }}</li>
                            <li>{{ $t("whatsappV3.resetWarning3") }}</li>
                        </ul>
                    </template>
                </a-alert>
                <p>{{ $t("whatsappV3.resetConfirmQuestion") }}</p>
            </div>
            <template #footer>
                <a-button key="back" @click="resetConfirmModal = false">
                    {{ $t("chatbotv2.dismiss") }}
                </a-button>
                <a-button key="submit" type="primary" @click="confirmResetChannel" :loading="resetLoadingChannelId !== null">
                    {{ $t("whatsappV3.confirmReset") }}
                </a-button>
            </template>
        </a-modal>
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import _ from "lodash";
import AppBreadCrumb from "../../../../components/common/AppBreadCrumb.vue";
import WhatsappIcon from "../../Chatbot/pages/integrations/svg/WhatsappIcon.vue";
import NewWhatsappV3Channel from "./NewWhatsappV3Channel.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import UpdateW3Channel from "./UpdateW3Channel.vue";
import WhatsappSolutionComparisonModal from "../../../../components/WhatsappSolutionComparisonModal.vue";
import UnipileChannelStatusBadge from "./UnipileChannelStatusBadge.vue";
import { resetWhatsappUnipileChannel } from "../../../../api/channel";

export default {
    name: "W3Channels",
    components: {
        WhatsappSolutionComparisonModal,
        UpdateW3Channel,
        NewWhatsappV3Channel,
        Modal2,
        AppBreadCrumb,
        WhatsappIcon,
        UnipileChannelStatusBadge,
    },
    data() {
        return {
            deleteModal: false,
            showCreateForm: false,
            updatingChannel: null,
            deleteChannelId: null,
            showInfoModal: false,
            resetLoadingChannelId: null,
            popupWindow: null,
            resetConfirmModal: false,
            resetConfirmChannel: null,
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels.filter((x) => x.type === "whatsapp-unipile"),
            isAdmin: (state) => state.auth.adminUser !== null,
            user: (state) => state.auth.user,
        }),
    },
    methods: {
        ...mapGetters({
            isChannelVisibleForMe: "channel/isChannelVisibleForMe",
        }),
        ...mapActions({
            listChannels: "channel/listChannels",
            listStories: "CB/listStories",
            deleteChannel: "channel/deleteChannel",
        }),
        copyChannelId(channelId) {
            navigator.clipboard
                .writeText(channelId)
                .then(() => {
                    this.$message.success(this.$t("whatsappV3.copyChannelIdSuccess"));
                })
                .catch(() => {
                    this.$message.error(this.$t("whatsappV3.copyChannelIdError"));
                });
        },
        deleteWpBusiness(id) {
            this.deleteModal = true;
            this.deleteChannelId = id;
        },
        createChannel() {
            this.showInfoModal = true;
        },
        editChannel(channel) {
            this.$router.push(`/integrations/whatsapp-v3/${channel._id}/detail`);
        },
        deleteWhatsappChannel() {
            this.deleteModal = false;
            this.deleteChannel(this.deleteChannelId);
        },
        isChannelAccessible(channel) {
            return this.isAdmin || _.get(this.user, "isOwner", false) || this.isChannelVisibleForMe(channel);
        },
        handleInfoCancel() {
            this.showInfoModal = false;
        },
        handleInfoAccept() {
            this.showInfoModal = false;
            this.showCreateForm = true;
        },
        needsReconnect(channel) {
            const rawStatus = _.get(channel, "payload.sources.0.status", "WAITING");
            const connectedStates = ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"];
            return !connectedStates.includes(rawStatus);
        },
        handleResetChannel(channel) {
            this.resetConfirmChannel = channel;
            this.resetConfirmModal = true;
        },
        async confirmResetChannel() {
            if (!this.resetConfirmChannel) return;

            this.resetLoadingChannelId = this.resetConfirmChannel._id;
            try {
                const response = await resetWhatsappUnipileChannel(this.resetConfirmChannel._id);
                this.resetConfirmModal = false;
                await this.listChannels();
                if (response.reconnectUrl) {
                    this.$message.success(this.$t("whatsappV3.reconnectStarting"));
                    this.openOnboardingLinkWithUrl(response.reconnectUrl);
                } else if (response.onboardLink) {
                    this.$message.success(this.$t("whatsappV3.channelResetStarting"));
                    this.openOnboardingLinkWithUrl(response.onboardLink);
                }
            } catch (error) {
                console.error("Reset channel error:", error);
                this.$message.error(this.$t("whatsappV3.resetChannelError"));
            } finally {
                this.resetLoadingChannelId = null;
                this.resetConfirmChannel = null;
            }
        },
        openOnboardingLinkWithUrl(url) {
            try {
                const parsedUrl = new URL(url);
                const width = 1000;
                const height = 800;
                const left = (window.screen.width - width) / 2;
                const top = (window.screen.height - height) / 2;

                const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes,location=no,menubar=no,toolbar=no`;
                this.popupWindow = window.open(parsedUrl.toString(), "WhatsappOnboarding", features);

                const checkWindow = setInterval(() => {
                    if (this.popupWindow && this.popupWindow.closed) {
                        clearInterval(checkWindow);
                        this.popupWindow = null;
                        this.listChannels();
                    }
                }, 500);
            } catch (error) {
                console.error("Geçersiz URL:", error);
                this.$message.error(this.$t("whatsappV3.invalidLinkUrl"));
            }
        },
    },
    mounted() {
        this.listChannels();
        this.listStories();
    },
};
</script>

<style lang="scss" scoped>
#w3-channels {
    width: 100%;
    min-height: calc(100vh - 50px);
    background: var(--color-bg-default);

    .channel-item.disabled {
        opacity: 0.5;
        pointer-events: none;
    }

    .big-title {
        font-weight: bold;
        font-size: 1.5rem;
        margin: 1rem 0;
    }

    .channels {
        display: flex;
        align-items: flex-start;
        padding-bottom: 1rem;
        flex-wrap: wrap;
        gap: 1rem;

        .story {
            background: #fff;
            border: 1px solid #a4a4a4;
            border-radius: 4px;
            padding: 1rem;
            cursor: pointer;
            transition: all 0.3s ease-in-out;
            width: 280px;
            height: 260px;
            min-width: 250px;
            max-width: 250px;
            margin-top: 10px;

            &:hover {
                border: 1px solid #06a9f3;
            }

            .title {
                margin: 1rem 0;
                font-weight: bold;
            }

            .chatbot-btn {
                border: none;
                background: #fff;
                border-radius: 4px;
                padding: 4px 8px;
                margin: 0;
                min-height: 34px;
                min-width: 34px;
                color: #000;
                display: flex;
                align-items: center;
                box-shadow: none !important;
                text-shadow: none !important;

                span {
                    text-align: left;
                }
            }
        }

        .channel-item {
            position: relative;
            background: #fff;
            border: 1px solid #a4a4a4;
            border-radius: 4px;
            padding: 1rem;
            cursor: pointer;
            transition: all 0.3s ease-in-out;
            width: 280px;
            height: 260px;
            min-width: 250px;
            max-width: 250px;
            text-align: center;
            transition: 500ms;
            margin-top: 10px;
            display: flex;
            flex-direction: column;

            .qr-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.85);
                border-radius: 4px;
                display: flex;
                align-items: center;
                justify-content: center;
                opacity: 0;
                transition: opacity 0.3s ease;
                z-index: 10;
                cursor: pointer;

                .qr-overlay-content {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                    color: #fff;

                    span {
                        font-size: 18px;
                        font-weight: bold;
                        color: #fff !important;
                    }
                }
            }

            &:hover .qr-overlay {
                opacity: 1;
            }

            .web-hook-title {
                word-break: break-all;
            }

            &:hover {
                border: 1px solid #06a9f3;

                span {
                    color: #06a9f3;
                }

                transform: translateY(-10px);
                box-shadow: -4px 22px 11px -5px rgba(0, 0, 0, 0.1);

                .options {
                    .icon-container {
                        i {
                            transform: rotate(360deg);
                            color: #06a9f3;
                        }
                    }
                }
            }

            .icon-container {
                width: 70px;
                height: 70px;
                display: flex;
                margin: 0 auto;
                align-items: center;
                justify-content: center;
                border-radius: 6px;
                margin-top: 40px;
                overflow: hidden;
                position: relative;
                background: #58ca64;
                padding: 10px;
            }

            .name-container {
                display: flex;
                flex-direction: column;
                gap: 5px;
                font-size: 20px;
                font-weight: bold;
                margin-top: 10px !important;
                flex: 1;

                .name {
                    margin-top: 0 !important;
                }

                .phone {
                    font-size: 14px;
                    font-weight: normal;
                    color: #a4a4a4;
                    margin-top: auto !important;
                    margin-bottom: 5px;
                }
            }

            .options {
                width: 100%;
                padding: 0 10px;
                align-items: center;
                position: absolute;
                top: 5px;
                right: 5px;
                display: flex;
                justify-content: space-between;
                z-index: 11;

                .icon-container {
                    background-color: transparent;
                    width: 25px;
                    height: 35px;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 3px;

                    i {
                        width: 100%;
                        height: 100%;
                        transition: 1s;
                    }
                }
            }
        }

        .channel-add {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: #006cff;
            border-color: #006cff;
            color: #fff;

            i {
                margin: 0;
            }
        }
    }
}
</style>
